<template>
  <div class="pack-card" :class="{ 'pack-card_disabled': isDisabled }">
    <div class="pack-card__main">
      <div class="pack-card__preview">
        <div class="pack-card__image">
          <img :src="packImageSrc" :alt="packName" />
        </div>
      </div>

      <div class="pack-card__content">
        <div class="pack-card__heading">
          <h3 class="pack-card__title" :title="packName">
            {{ packName }}
          </h3>

          <div class="pack-card__price">
            <span class="pack-card__price-value">
              {{ formattedPrice }}
            </span>

            <span class="pack-card__currency">
              {{ currency }}
            </span>
          </div>
        </div>

        <p class="pack-card__description" :title="description">
          {{ description }}
        </p>

        <div class="pack-card__actions">
          <span class="pack-card__items-count">
            {{ getItemsText(packProducts.length) }}
          </span>
          <button
            v-if="packProducts.length"
            type="button"
            class="pack-card__toggle button bordered"
            :aria-expanded="isExpanded"
            @click="toggleProducts"
          >
            <span>
              {{ isExpanded ? $t('СКРЫТЬ СОСТАВ') : $t('ПОКАЗАТЬ СОСТАВ') }}
            </span>
          </button>

          <button
            type="button"
            class="pack-card__buy button shadowed"
            :disabled="isLoading || isDisabled"
            @click="handlePurchase"
          >
            <span v-if="isLoading">
              {{ $t('ОБРАБОТКА...') }}
            </span>

            <span v-else-if="isAdded" class="pack-card__added">
              {{ $t('ДОБАВЛЕНО') }}
            </span>

            <span v-else>
              {{ $t('КУПИТЬ') }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <transition name="pack-products">
      <div v-if="isExpanded && packProducts.length" class="pack-card__products">
        <div
          v-for="product in packProducts"
          :key="product.id"
          class="pack-product"
        >
          <div class="pack-product__image">
            <img
              :src="getProductImage(product.image_url)"
              :alt="product.title"
            />
          </div>

          <div class="pack-product__title" :title="product.title">
            {{ product.title }}
          </div>
          <div class="pack-product__quantity">
            {{ getQuantityText(product.quantity) }}
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, ref } from 'vue'

export default {
  name: 'PackStoreItem',

  props: {
    packId: {
      type: [String, Number],
      required: true,
    },

    packName: {
      type: String,
      required: true,
    },

    description: {
      type: String,
      default: '',
    },

    price: {
      type: [String, Number],
      required: true,
    },

    currency: {
      type: String,
      default: 'EUR',
    },

    imageUrl: {
      type: String,
      required: true,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    packProducts: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['purchase'],

  setup(props, { emit }) {
    const imgDomain = process.env.VUE_APP_STATIC_DOMAIN || ''

    const isAdded = ref(false)
    const isExpanded = ref(false)
    let addedTimeout = null

    const normalizeImageUrl = (imageUrl) => {
      if (!imageUrl) {
        return ''
      }

      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl
      }

      const domain = imgDomain.replace(/\/+$/, '')
      const path = imageUrl.replace(/^\/+/, '')

      return domain ? `${domain}/${path}` : `/${path}`
    }

    const packImageSrc = computed(() => {
      return normalizeImageUrl(props.imageUrl)
    })

    const getQuantityText = (quantity) => {
      const count = Number(quantity) || 0
      const mod10 = count % 10
      const mod100 = count % 100

      if (mod10 === 1 && mod100 !== 11) {
        return `${count} штука`
      }

      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return `${count} штуки`
      }

      return `${count} штук`
    }

    const formattedPrice = computed(() => {
      const numericPrice = Number(props.price)

      if (!Number.isFinite(numericPrice)) {
        return '0.00'
      }

      return numericPrice.toFixed(2)
    })

    const toggleProducts = () => {
      if (!props.packProducts.length) {
        return
      }

      isExpanded.value = !isExpanded.value
    }

    const handlePurchase = () => {
      if (props.isLoading || props.isDisabled || isAdded.value) {
        return
      }

      emit('purchase', {
        packId: props.packId,
        packName: props.packName,
        price: Number(props.price),
        currency: props.currency,
        type: 'pack',
      })

      isAdded.value = true

      if (addedTimeout) {
        clearTimeout(addedTimeout)
      }

      addedTimeout = setTimeout(() => {
        isAdded.value = false
      }, 2000)
    }

    const getProductImage = (imageUrl) => {
      return normalizeImageUrl(imageUrl)
    }

    const getItemsText = (count) => {
      const mod10 = count % 10
      const mod100 = count % 100

      if (mod10 === 1 && mod100 !== 11) {
        return `${count} предмет`
      }

      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return `${count} предмета`
      }

      return `${count} предметов`
    }

    onBeforeUnmount(() => {
      if (addedTimeout) {
        clearTimeout(addedTimeout)
      }
    })

    return {
      formattedPrice,
      getProductImage,
      handlePurchase,
      isAdded,
      isExpanded,
      packImageSrc,
      toggleProducts,
      getItemsText,
      getQuantityText,
    }
  },
}
</script>

<style scoped lang="scss">
.pack-card {
  position: relative;
  border: 1px solid var(--outer-space);
  border-radius: 8px;
  background-color: var(--white-woodsmoke);
  overflow: visible;

  &::before {
    content: '';
    position: absolute;
    inset: 6px -6px -6px 6px;
    z-index: -1;
    border-radius: inherit;
    background-color: var(--heavy-metal);
    pointer-events: none;
  }

  &_disabled {
    opacity: 0.6;
  }
}

.pack-card__main {
  display: flex;
  align-items: stretch;
  min-height: 190px;
}

.pack-card__preview {
  flex: 0 0 190px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;

  background: radial-gradient(circle, #1d2723 20%, #121815 70%);

  box-shadow: inset 3px 3px 0 rgba(0, 0, 0, 0.5),
    inset -3px -3px 0 rgba(255, 255, 255, 0.05);
}

.pack-card__image {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.pack-card__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 24px;
}

.pack-card__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.pack-card__title {
  min-width: 0;
  margin: 0;

  font-family: var(--font-2);
  font-size: 20px;
  font-weight: 600;
  line-height: 26px;

  color: var(--gray-nurse);

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pack-card__price {
  flex-shrink: 0;
  display: flex;
  align-items: baseline;
  gap: 5px;
}

.pack-card__price-value,
.pack-card__currency {
  font-family: var(--font-2);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
}

.pack-card__price-value {
  color: var(--harvest-gold);
}

.pack-card__currency {
  color: var(--harvest-gold);
}

.pack-card__description {
  margin: 14px 0 0;

  font-family: var(--font-1);
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;

  color: var(--pewter);

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
}

.pack-card__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  margin-top: auto;
  padding-top: 22px;
}

.pack-card__items-count {
  color: var(--harvest-gold);
  font-family: var(--font-2);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
}

.pack-card__toggle,
.pack-card__buy {
  min-height: 42px;
  justify-content: center;
  text-transform: uppercase;
}

.pack-card__toggle {
  min-width: 180px;
  font-family: var(--font-1);
  font-size: 12px;
  font-weight: 700;
}

.pack-card__buy {
  min-width: 170px;
  padding: 12px 24px;

  font-family: var(--font-3);
  font-size: 10px;
  line-height: 14px;
}

.pack-card__added {
  color: inherit;
}

.pack-card__products {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 16px;
  border-top: 1px solid var(--outer-space);
  background: var(--bunker-86);
}

.pack-product {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;

  border: 1px solid var(--outer-space);
  border-radius: 8px;
  background: var(--heavy-metal);
}

.pack-product__image {
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  background: radial-gradient(circle, #1d2723 20%, #121815 70%);

  box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.4),
    inset -2px -2px 0 rgba(255, 255, 255, 0.04);

  img {
    display: block;
    width: 38px;
    height: 38px;
    object-fit: contain;
  }
}

.pack-product__title {
  min-width: 0;

  font-family: var(--font-2);
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;

  color: var(--gray-nurse);

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pack-product__quantity {
  margin-left: auto;
  margin-top: 2px;
  font-family: var(--font-1);
  font-size: 14px;
  font-weight: 400;
  color: var(--harvest-gold);
  white-space: nowrap;
}

.pack-products-enter-active,
.pack-products-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.pack-products-enter-from,
.pack-products-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 1100px) {
  .pack-card__preview {
    flex-basis: 160px;
  }

  .pack-card__products {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767.98px) {
  .pack-card__main {
    flex-direction: column;
  }

  .pack-card__preview {
    flex-basis: auto;
    min-height: 170px;
  }

  .pack-card__heading {
    flex-direction: column;
    gap: 10px;
  }

  .pack-card__title {
    white-space: normal;
  }

  .pack-card__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .pack-card__toggle,
  .pack-card__buy {
    width: 100%;
    min-width: 0;
  }

  .pack-card__products {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 479.98px) {
  .pack-card__content {
    padding: 16px;
  }

  .pack-card__products {
    grid-template-columns: 1fr;
  }

  .pack-product__title {
    white-space: normal;
  }
}
</style>
